import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import image1 from "../../images/commentcamarche/image_presta.png";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { FaRocket, FaCheck, FaStar, FaUsers, FaChartLine } from "react-icons/fa";
import { EASING } from "../../utils/easingUtils";

interface CallToActionHeaderProps {
  title?: string;
  highlightText?: string;
  description?: string;
  buttonText?: string;
  buttonAction?: () => void;
  imageSrc?: string;
  highlightTextColor?: string;
  imageWidth?: string;
  imageHeight?: string;
  bg?: string;
  Htitle?: keyof JSX.IntrinsicElements;
}

const benefits = [
  { icon: FaRocket, text: "Développez votre activité événementielle" },
  { icon: FaCheck, text: "Présentez votre savoir-faire" },
  { icon: FaStar, text: "Attirez des clients qualifiés à Paris et en région" },
];

const stats = [
  { number: "500+", label: "Prestataires actifs", color: "text-pink-600" },
  { number: "2000+", label: "Événements réussis", color: "text-rose-600" },
  { number: "95%", label: "Satisfaction client", color: "text-purple-600" },
];

const CallToActionHeader: React.FC<CallToActionHeaderProps> = ({
  Htitle = "h1",
  title = "Proposez vos prestations événementielles et gagnez en visibilité",
  highlightText = "prestations",
  description = `Vous êtes prestataire événementiel à Paris, en Île-de-France et partout en France? Rejoignez EventToBee, la plateforme dédiée aux professionnels de l'événementiel souhaitant gagner en visibilité et trouver de nouveaux clients.`,
  buttonText = "S'inscrire",
  buttonAction,
  imageSrc = image1,
  highlightTextColor = "bg-pink-500",
  imageWidth = "w-full",
  imageHeight = "h-full",
  bg = "bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900",
}) => {
  const navigate = useNavigate();
  const defaultAction = () => navigate("/signup/service");

  return (
    <div className={`relative overflow-hidden ${bg}`}>
      {/* Background Effects */}
      <BgGlassmorphism />
      <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

      <div className="container relative px-4 py-16 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left Section: Content */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: EASING.easeOut }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <FaRocket className="w-4 h-4 mr-2 text-pink-500" />
              Rejoignez notre réseau
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <Htitle className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white leading-tight">
                {title.split(highlightText).map((part, index, array) =>
                  index < array.length - 1 ? (
                    <span key={index}>
                      {part}
                      <span className="relative inline-block">
                        <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
                          {highlightText}
                        </span>
                        <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full"></span>
                      </span>
                    </span>
                  ) : (
                    part
                  )
                )}
              </Htitle>
            </motion.div>

            {/* Description */}
            <motion.p
              className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {description}
            </motion.p>

            {/* Benefits List */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
                    <benefit.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Statistics */}
            <motion.div
              className="grid grid-cols-3 gap-4 py-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center p-4 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className={`text-2xl font-bold ${stat.color} mb-1`}>{stat.number}</div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-400">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
            >
              <button
                className="group bg-gradient-to-r from-pink-500 to-rose-500 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={buttonAction || defaultAction}
              >
                <span className="flex items-center">
                  {buttonText}
                  <svg
                    className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    ></path>
                  </svg>
                </span>
              </button>
            </motion.div>
          </motion.div>

          {/* Right Section: Modern Illustration or Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: EASING.easeOut }}
          >
            <div className="relative group">
              {/* Decorative Elements */}
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-pink-400/30 to-rose-400/30 rounded-full blur-xl group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-xl group-hover:scale-110 transition-transform duration-500"></div>

              {/* Modern Illustration or Image Container */}
              <div className="relative overflow-hidden transform group-hover:scale-105 transition-transform duration-500">
                {imageSrc === "modern-illustration" ? (
                  /* Modern SVG Illustration */
                  <div className="w-full h-[400px] md:h-[500px] flex items-center justify-center relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-10 left-10 w-20 h-20 bg-pink-500 rounded-full animate-pulse"></div>
                      <div className="absolute top-32 right-16 w-16 h-16 bg-blue-500 rounded-lg animate-bounce"></div>
                      <div className="absolute bottom-20 left-20 w-12 h-12 bg-purple-500 rounded-full animate-ping"></div>
                      <div className="absolute bottom-32 right-32 w-14 h-14 bg-yellow-500 rounded-lg animate-pulse"></div>
                    </div>

                    {/* Main Illustration */}
                    <div className="relative z-10 flex flex-col items-center space-y-8">
                      {/* Central Building Icon */}
                      <motion.div
                        className="relative"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.8, duration: 0.6, type: "spring" }}
                      >
                        <div className="w-32 h-32 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl">
                          <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" clipRule="evenodd" />
                          </svg>
                        </div>
                        {/* Glow Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl blur-xl opacity-30 animate-pulse"></div>
                      </motion.div>

                      {/* Floating Elements */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {/* Revenue Growth */}
                        <motion.div
                          className="absolute -top-8 -left-8 bg-white dark:bg-gray-700 rounded-2xl p-4 shadow-lg"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.2, duration: 0.6 }}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                              <FaChartLine className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Revenus</div>
                              <div className="text-sm font-bold text-green-600">+250%</div>
                            </div>
                          </div>
                        </motion.div>

                        {/* Reservations */}
                        <motion.div
                          className="absolute -top-8 -right-8 bg-white dark:bg-gray-700 rounded-2xl p-4 shadow-lg"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.4, duration: 0.6 }}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                              <FaUsers className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Réservations</div>
                              <div className="text-sm font-bold text-blue-600">1,247</div>
                            </div>
                          </div>
                        </motion.div>

                        {/* Rating */}
                        <motion.div
                          className="absolute -bottom-8 -left-8 bg-white dark:bg-gray-700 rounded-2xl p-4 shadow-lg"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.6, duration: 0.6 }}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                              <FaStar className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Note moyenne</div>
                              <div className="text-sm font-bold text-yellow-600">4.9/5</div>
                            </div>
                          </div>
                        </motion.div>

                        {/* Visibility */}
                        <motion.div
                          className="absolute -bottom-8 -right-8 bg-white dark:bg-gray-700 rounded-2xl p-4 shadow-lg"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.8, duration: 0.6 }}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                              <FaRocket className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Visibilité</div>
                              <div className="text-sm font-bold text-purple-600">Max</div>
                            </div>
                          </div>
                        </motion.div>
                      </div>

                      {/* Success Indicator */}
                      <motion.div
                        className="flex items-center space-x-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 2, duration: 0.6 }}
                      >
                        <FaCheck className="w-4 h-4" />
                        <span className="text-sm font-medium">Espace vérifié et optimisé</span>
                      </motion.div>
                    </div>
                  </div>
                ) : (
                  /* Traditional Image */
                  <img
                    src={imageSrc}
                    alt="Prestataire événementiel"
                    className="w-full h-[400px] md:h-[500px] object-cover"
                  />
                )}

                {/* Floating Badge */}
                <motion.div
                  className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.5, duration: 0.5 }}
                >
                  <div className="flex items-center space-x-2">
                    <FaUsers className="w-4 h-4 text-pink-500" />
                    <span className="text-sm font-medium text-neutral-700">Professionnel vérifié</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CallToActionHeader;
